// Health Configuration and Calculation System
// This file contains all health-related calculations, risk assessments, and recommendations

/**
 * Calculate BMI (Body Mass Index)
 * @param {number} weight - Weight in kg
 * @param {number} height - Height in cm
 * @returns {number} BMI value
 */
export const calculateBMI = (weight, height) => {
  if (!weight || !height) return 0;
  const heightInMeters = height / 100;
  return parseFloat((weight / (heightInMeters * heightInMeters)).toFixed(1));
};

/**
 * Get BMI category
 * @param {number} bmi - BMI value
 * @returns {object} Category and risk level
 */
export const getBMICategory = (bmi) => {
  if (bmi < 18.5)
    return { category: "Underweight", risk: "moderate", color: "#ffaa00" };
  if (bmi < 25) return { category: "Normal", risk: "low", color: "#00ff88" };
  if (bmi < 30)
    return { category: "Overweight", risk: "moderate", color: "#ffaa00" };
  return { category: "Obese", risk: "high", color: "#ff4444" };
};

/**
 * Calculate BMR (Basal Metabolic Rate) using Mifflin-St Jeor Equation
 * @param {number} weight - Weight in kg
 * @param {number} height - Height in cm
 * @param {number} age - Age in years
 * @param {string} gender - 'male' or 'female'
 * @returns {number} BMR in calories
 */
export const calculateBMR = (weight, height, age, gender) => {
  if (!weight || !height || !age) return 0;

  const bmr = 10 * weight + 6.25 * height - 5 * age;
  return Math.round(gender === "male" ? bmr + 5 : bmr - 161);
};

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 * @param {number} bmr - Basal Metabolic Rate
 * @param {string} activityLevel - Activity level
 * @returns {number} TDEE in calories
 */
export const calculateTDEE = (bmr, activityLevel) => {
  const multipliers = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
  };
  return Math.round(bmr * (multipliers[activityLevel] || 1.2));
};

/**
 * Calculate ideal weight range
 * @param {number} height - Height in cm
 * @param {string} gender - 'male' or 'female'
 * @returns {object} Min and max ideal weight
 */
export const calculateIdealWeightRange = (height, gender) => {
  const heightInMeters = height / 100;
  const minBMI = 18.5;
  const maxBMI = 24.9;

  return {
    min: Math.round(minBMI * heightInMeters * heightInMeters),
    max: Math.round(maxBMI * heightInMeters * heightInMeters),
  };
};

/**
 * Calculate recommended water intake
 * @param {number} weight - Weight in kg
 * @param {string} activityLevel - Activity level
 * @returns {number} Water in liters
 */
export const calculateWaterIntake = (weight, activityLevel) => {
  const baseIntake = weight * 0.033; // 33ml per kg
  const activityBonus = {
    sedentary: 0,
    light: 0.5,
    moderate: 0.7,
    active: 1.0,
    very_active: 1.5,
  };
  return parseFloat(
    (baseIntake + (activityBonus[activityLevel] || 0)).toFixed(1)
  );
};

/**
 * Calculate recommended sleep duration
 * @param {number} age - Age in years
 * @returns {object} Min and max sleep hours
 */
export const calculateSleepDuration = (age) => {
  if (age < 18) return { min: 8, max: 10 };
  if (age < 65) return { min: 7, max: 9 };
  return { min: 7, max: 8 };
};

/**
 * Calculate target heart rate zones
 * @param {number} age - Age in years
 * @returns {object} Heart rate zones
 */
export const calculateHeartRateZones = (age) => {
  const maxHR = 220 - age;
  return {
    resting: { min: 60, max: 100 },
    fatBurn: { min: Math.round(maxHR * 0.5), max: Math.round(maxHR * 0.7) },
    cardio: { min: Math.round(maxHR * 0.7), max: Math.round(maxHR * 0.85) },
    peak: { min: Math.round(maxHR * 0.85), max: maxHR },
  };
};

/**
 * Assess cardiovascular risk
 * @param {object} userData - User health data
 * @returns {object} Risk assessment
 */
export const assessCardiovascularRisk = (userData) => {
  let score = 100;
  const factors = [];

  // BMI factor
  const bmi = calculateBMI(userData.weight, userData.height);
  if (bmi >= 30) {
    score -= 20;
    factors.push("high_bmi");
  } else if (bmi >= 25) {
    score -= 10;
    factors.push("elevated_bmi");
  }

  // Age factor
  if (userData.age > 65) {
    score -= 15;
    factors.push("advanced_age");
  } else if (userData.age > 45) {
    score -= 5;
    factors.push("middle_age");
  }

  // Activity level
  if (userData.activityLevel === "sedentary") {
    score -= 15;
    factors.push("sedentary_lifestyle");
  } else if (
    userData.activityLevel === "active" ||
    userData.activityLevel === "very_active"
  ) {
    factors.push("active_lifestyle");
  }

  // Chronic conditions
  if (
    userData.healthConditions?.chronicConditions?.some(
      (c) => c.name === "hypertension"
    )
  ) {
    score -= 20;
    factors.push("hypertension");
  }
  if (
    userData.healthConditions?.chronicConditions?.some(
      (c) => c.name === "heartDisease"
    )
  ) {
    score -= 30;
    factors.push("heart_disease");
  }

  const risk =
    score >= 80
      ? "low"
      : score >= 60
      ? "moderate"
      : score >= 40
      ? "high"
      : "critical";

  return { score: Math.max(0, score), risk, factors };
};

/**
 * Assess metabolic health risk
 * @param {object} userData - User health data
 * @returns {object} Risk assessment
 */
export const assessMetabolicRisk = (userData) => {
  let score = 100;
  const factors = [];

  const bmi = calculateBMI(userData.weight, userData.height);

  // BMI factor
  if (bmi >= 30) {
    score -= 25;
    factors.push("obesity");
  } else if (bmi >= 25) {
    score -= 15;
    factors.push("overweight");
  } else if (bmi < 18.5) {
    score -= 10;
    factors.push("underweight");
  }

  // Diabetes
  if (
    userData.healthConditions?.chronicConditions?.some(
      (c) => c.name === "diabetes"
    )
  ) {
    score -= 30;
    factors.push("diabetes");
  }

  // Diet quality (if tracked)
  if (userData.healthInfo?.nutrition?.dietType === "poor") {
    score -= 15;
    factors.push("poor_diet");
  }

  const risk =
    score >= 80
      ? "low"
      : score >= 60
      ? "moderate"
      : score >= 40
      ? "high"
      : "critical";

  return { score: Math.max(0, score), risk, factors };
};

/**
 * Assess mental wellness
 * @param {object} userData - User health data
 * @returns {object} Risk assessment
 */
export const assessMentalWellness = (userData) => {
  let score = 100;
  const factors = [];

  // Sleep quality
  const sleepHours = userData.healthInfo?.sleep?.hours || 0;
  const recommendedSleep = calculateSleepDuration(userData.age);

  if (sleepHours < recommendedSleep.min - 1) {
    score -= 20;
    factors.push("poor_sleep");
  } else if (
    sleepHours >= recommendedSleep.min &&
    sleepHours <= recommendedSleep.max
  ) {
    factors.push("good_sleep");
  }

  // Stress level
  if (userData.healthInfo?.mentalHealth === "high_stress") {
    score -= 25;
    factors.push("high_stress");
  } else if (userData.healthInfo?.mentalHealth === "moderate_stress") {
    score -= 10;
    factors.push("moderate_stress");
  } else {
    factors.push("low_stress");
  }

  // Activity (exercise helps mental health)
  if (
    userData.activityLevel === "active" ||
    userData.activityLevel === "very_active"
  ) {
    factors.push("regular_exercise");
  } else if (userData.activityLevel === "sedentary") {
    score -= 10;
    factors.push("inactive");
  }

  const risk =
    score >= 80
      ? "low"
      : score >= 60
      ? "moderate"
      : score >= 40
      ? "high"
      : "critical";

  return { score: Math.max(0, score), risk, factors };
};

/**
 * Assess physical fitness
 * @param {object} userData - User health data
 * @returns {object} Risk assessment
 */
export const assessPhysicalFitness = (userData) => {
  let score = 100;
  const factors = [];

  // Activity level
  if (userData.activityLevel === "very_active") {
    factors.push("very_active");
  } else if (userData.activityLevel === "active") {
    factors.push("active");
  } else if (userData.activityLevel === "moderate") {
    score -= 10;
    factors.push("moderate_activity");
  } else if (userData.activityLevel === "sedentary") {
    score -= 30;
    factors.push("sedentary");
  }

  // BMI
  const bmi = calculateBMI(userData.weight, userData.height);
  const bmiCat = getBMICategory(bmi);
  if (bmiCat.category === "Normal") {
    factors.push("healthy_weight");
  } else {
    score -= 15;
    factors.push("weight_concern");
  }

  // Exercise frequency
  const exerciseFreq =
    userData.healthInfo?.physicalFitness?.exerciseFrequency || "never";
  if (exerciseFreq === "daily" || exerciseFreq === "5-6_times") {
    factors.push("regular_exercise");
  } else if (exerciseFreq === "never" || exerciseFreq === "1-2_times") {
    score -= 20;
    factors.push("insufficient_exercise");
  }

  const risk =
    score >= 80
      ? "low"
      : score >= 60
      ? "moderate"
      : score >= 40
      ? "high"
      : "critical";

  return { score: Math.max(0, score), risk, factors };
};

/**
 * Calculate overall health score
 * @param {object} userData - User health data
 * @returns {object} Overall assessment
 */
export const calculateOverallHealthScore = (userData) => {
  const cardio = assessCardiovascularRisk(userData);
  const metabolic = assessMetabolicRisk(userData);
  const mental = assessMentalWellness(userData);
  const fitness = assessPhysicalFitness(userData);

  const overallScore = Math.round(
    (cardio.score + metabolic.score + mental.score + fitness.score) / 4
  );
  const overallRisk =
    overallScore >= 80
      ? "low"
      : overallScore >= 60
      ? "moderate"
      : overallScore >= 40
      ? "high"
      : "critical";

  return {
    overallScore,
    overallRisk,
    categories: {
      cardiovascular: cardio,
      metabolic: metabolic,
      mental: mental,
      fitness: fitness,
    },
  };
};

/**
 * Generate health recommendations
 * @param {object} riskAssessment - Risk assessment results
 * @param {object} userData - User health data
 * @returns {array} Recommendations
 */
export const generateRecommendations = (riskAssessment, userData) => {
  const recommendations = [];

  // Water intake
  const recommendedWater = calculateWaterIntake(
    userData.weight,
    userData.activityLevel
  );
  recommendations.push({
    category: "hydration",
    priority: "medium",
    message: `Drink ${recommendedWater}L of water daily`,
    icon: "💧",
  });

  // Sleep
  const sleepRec = calculateSleepDuration(userData.age);
  if (userData.healthInfo?.sleep?.hours < sleepRec.min) {
    recommendations.push({
      category: "sleep",
      priority: "high",
      message: `Aim for ${sleepRec.min}-${sleepRec.max} hours of sleep`,
      icon: "😴",
    });
  }

  // Exercise
  if (riskAssessment.categories.fitness.risk !== "low") {
    recommendations.push({
      category: "exercise",
      priority: "high",
      message: "Add 30 minutes of moderate exercise daily",
      icon: "🏃",
    });
  }

  // Weight management
  const bmi = calculateBMI(userData.weight, userData.height);
  if (bmi >= 25) {
    recommendations.push({
      category: "weight",
      priority: "high",
      message:
        "Consider a balanced diet and regular exercise for weight management",
      icon: "⚖️",
    });
  }

  // Cardiovascular
  if (
    riskAssessment.categories.cardiovascular.risk === "high" ||
    riskAssessment.categories.cardiovascular.risk === "critical"
  ) {
    recommendations.push({
      category: "cardiovascular",
      priority: "critical",
      message: "Consult a healthcare provider for cardiovascular assessment",
      icon: "❤️",
    });
  }

  // Mental wellness
  if (riskAssessment.categories.mental.risk !== "low") {
    recommendations.push({
      category: "mental",
      priority: "medium",
      message: "Practice stress management techniques like meditation",
      icon: "🧘",
    });
  }

  return recommendations;
};

/**
 * Health goal presets
 */
export const GOAL_PRESETS = {
  weight_loss: {
    name: "Weight Loss",
    steps: 10000,
    sleep: 8,
    calories: -500, // deficit
    water: 3.0,
    workouts: 5,
  },
  muscle_gain: {
    name: "Muscle Gain",
    steps: 8000,
    sleep: 8,
    calories: 300, // surplus
    water: 3.5,
    workouts: 5,
  },
  maintenance: {
    name: "Maintenance",
    steps: 8000,
    sleep: 7.5,
    calories: 0,
    water: 2.5,
    workouts: 3,
  },
  wellness: {
    name: "General Wellness",
    steps: 10000,
    sleep: 8,
    calories: 0,
    water: 2.5,
    workouts: 4,
  },
};

/**
 * Tracker categories configuration
 */
export const TRACKER_CATEGORIES = [
  {
    id: "exercise",
    name: "Exercise",
    icon: "🏃",
    color: "#00d9ff",
    metrics: ["steps", "workouts", "active_minutes"],
  },
  {
    id: "nutrition",
    name: "Nutrition",
    icon: "🥗",
    color: "#00ff88",
    metrics: ["calories", "water", "meals"],
  },
  {
    id: "wellbeing",
    name: "Wellbeing",
    icon: "🧘",
    color: "#ff88ff",
    metrics: ["sleep", "mood", "stress", "energy"],
  },
  {
    id: "body_comp",
    name: "Body Comp",
    icon: "⚖️",
    color: "#ffaa00",
    metrics: ["weight", "bmi", "muscle_mass", "body_fat"],
  },
];

export default {
  calculateBMI,
  getBMICategory,
  calculateBMR,
  calculateTDEE,
  calculateIdealWeightRange,
  calculateWaterIntake,
  calculateSleepDuration,
  calculateHeartRateZones,
  assessCardiovascularRisk,
  assessMetabolicRisk,
  assessMentalWellness,
  assessPhysicalFitness,
  calculateOverallHealthScore,
  generateRecommendations,
  GOAL_PRESETS,
  TRACKER_CATEGORIES,
};
